import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CircleNotch as Loader2,
  MagnifyingGlass as Search,
  X,
  Sparkle as Sparkles,
} from '@phosphor-icons/react';
import { searchCarInventory } from '../services/api';
import CarSuggestionThumbnail from './ui/CarSuggestionThumbnail';
import { formatRegYear } from '../utils/registrationYear';
import { deriveSuggestions } from '../utils/reservedCars';
import { matchCarsByQuery } from '../utils/localCarSearch';
import { trackSearchSubmit } from '../services/analyticsBridge';
import { useTranslation } from '../i18n/I18nContext';
import { useTranslateVehicleValue } from '../utils/vehicleValues';
import { config } from '../config/runtimeConfig';
import { formatEuro, numberLocale } from '../utils/formatMoney';
import './SearchBar.css';

// Rows rendered in the suggestions dropdown before "Show all N results".
const SUGGESTION_LIMIT = 6;

/**
 * Isolated search bar component for Meilisearch instant search.
 * Has its own input state — typing does NOT trigger parent re-render.
 *
 * Variants:
 * - "full" (default): Full-width search bar for catalog page. Suggestion click → car detail.
 * - "standalone": For homepage embedding. Suggestion click → navigate to catalog with search query.
 * - "compact": For nav menu. Icon button that expands into input on click.
 *
 * @param {Function} onSearchResults - Called with Meilisearch result or null (only for "full" variant)
 * @param {Object} voiceAssistant - Voice assistant context (optional)
 * @param {string} initialValue - Initial search input value
 * @param {string} variant - "full" | "standalone" | "compact"
 * @param {string} catalogPath - Path to catalog page (for standalone/compact, default: "/cars")
 * @param {string} placeholder - Custom placeholder text
 * @param {boolean} force - Искать даже когда поиск не включён в widget-config
 *   дилера. Нужен местам, которые ставят строку поиска осознанно — шапка
 *   витрины, search-only embed. По умолчанию false: у дилеров всё как было.
 * @param {Array} fallbackCars - Сток, по которому искать, когда сервер
 *   отвечает `available: false` (Meilisearch выключен или недоступен, #1344).
 *   Пустой по умолчанию: у дилеров поведение прежнее — строка честно говорит,
 *   что поиск недоступен, и ничего не выдумывает.
 * @param {Function} onSubmitQuery - Отправка запроса в выдачу: Enter, «Show
 *   all» и крестик зовут её с запросом (крестик — с пустой строкой). Для
 *   каталога, который держит запрос в адресе (`?search=`) и сам по нему
 *   фильтрует. Без неё "full" только показывает подсказки, как раньше.
 *   Enter при ней не отправляет форму, внутри которой стоит строка: запрос
 *   уходит один раз, через колбэк.
 * @param {Function} onQueryChange - Текст поля при каждом изменении (крестик —
 *   пустая строка). Для формы, которая учитывает запрос в своём счётчике.
 */
const SearchBar = ({
  onSearchResults,
  voiceAssistant,
  initialValue = '',
  variant = 'full',
  catalogPath = '/cars',
  placeholder,
  force = false,
  fallbackCars = [],
  onSubmitQuery,
  onQueryChange,
}) => {
  const navigate = useNavigate();
  const { t, language } = useTranslation();
  const translateVehicleValue = useTranslateVehicleValue();
  // Цена и пробег в подсказках на витрине — по языку, как в каталоге и на
  // странице машины (#1335). У дилеров формат прежний.
  const money = config.IS_OWN_STOCK
    ? (value) => formatEuro(value, language)
    : (value) => `€${value.toLocaleString()}`;
  const plainNumber = config.IS_OWN_STOCK
    ? (value) => value?.toLocaleString(numberLocale(language))
    : (value) => value?.toLocaleString();
  const [inputValue, setInputValue] = useState(initialValue);
  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [unavailable, setUnavailable] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [expanded, setExpanded] = useState(false); // compact variant
  const debounceRef = useRef(null);
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const lastEmittedQueryRef = useRef(''); // #1133: dedupe search-submit analytics
  const hasUserTypedRef = useRef(false); // #1133: only count user-initiated searches

  const isCompact = variant === 'compact';
  const isStandalone = variant === 'standalone';

  /**
   * Поиск по загруженному стоку — запасной путь, когда сервер отвечает
   * `available: false`. Само правило отбора живёт в `utils/localCarSearch`:
   * оно чистое и проверяется тестом, а не прогоном интерфейса.
   */
  const searchLoadedStock = useCallback(
    (query) => {
      const hits = matchCarsByQuery(fallbackCars, query);
      return { hits, estimatedTotalHits: hits.length, offline: true };
    },
    [fallbackCars]
  );

  // Debounced search: input → 300ms → API → notify parent
  const performSearch = useCallback(
    async (query) => {
      if (!query || query.trim().length < 2) {
        setResults(null);
        setSearching(false);
        if (onSearchResults) onSearchResults(null);
        return;
      }

      setSearching(true);
      try {
        const trimmed = query.trim();
        const result = await searchCarInventory(trimmed, { limit: 100, force });
        // Сервер отвечает `available: false`, когда Meilisearch выключен или
        // недоступен. Без этой ветки строка молча ничего не показывала, и в
        // демо это читалось как сломанное поле (#1344).
        if (result.available !== false) {
          setUnavailable(false);
          setResults(result);
          if (onSearchResults) onSearchResults(result);
          // #1133: emit a search-submit event for the CS Analytics "AI Search
          // Submits" metric. Gated on hasUserTypedRef so ONLY user-initiated
          // searches count — never the mount/deep-link/preset-driven run, which
          // would inflate the metric with passive landings. Deduped per distinct
          // query so identical re-runs don't double-count. Best-effort — pushEvent
          // never throws.
          if (
            hasUserTypedRef.current &&
            trimmed !== lastEmittedQueryRef.current
          ) {
            lastEmittedQueryRef.current = trimmed;
            trackSearchSubmit({
              query: trimmed,
              resultCount: result.estimatedTotalHits ?? result.hits?.length,
              variant,
            });
          }
        } else {
          // Сервер не отвечает поиском — ищем по загруженному стоку, если он у
          // нас есть. Аналитику отсюда НЕ шлём: метрика считает обращения к
          // поиску продукта, а это запасной путь витрины, и смешивать их
          // значило бы отчитываться о работе выключенного сервиса.
          const local = fallbackCars.length ? searchLoadedStock(trimmed) : null;
          setUnavailable(!local);
          setResults(local);
          if (onSearchResults) onSearchResults(local);
        }
      } catch {
        setResults(null);
        if (onSearchResults) onSearchResults(null);
      } finally {
        setSearching(false);
      }
    },
    [onSearchResults, variant, force, fallbackCars, searchLoadedStock]
  );

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => performSearch(inputValue), 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [inputValue, performSearch]);

  // Sync external initialValue → inputValue when parent's URL-derived value changes.
  // Without this, navigating /cars → /cars?search=X (same component instance) would
  // leave the input empty and Meilisearch wouldn't fire.
  // Guard: only setState when value actually differs — avoids clobbering the user's
  // in-progress typing on parent re-renders that didn't change initialValue.
  useEffect(() => {
    setInputValue((prev) => (prev === initialValue ? prev : initialValue));
  }, [initialValue]);

  // Click outside → close suggestions / collapse compact
  useEffect(() => {
    const handler = (e) => {
      const path = e.composedPath ? e.composedPath() : [e.target];
      if (containerRef.current && !path.includes(containerRef.current)) {
        setShowSuggestions(false);
        if (isCompact && !inputValue) setExpanded(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isCompact, inputValue]);

  const handleClear = () => {
    setInputValue('');
    lastEmittedQueryRef.current = ''; // #1133: let a re-search of the same term count again
    setResults(null);
    setShowSuggestions(false);
    if (onSearchResults) onSearchResults(null);
    if (onSubmitQuery) onSubmitQuery('');
    if (onQueryChange) onQueryChange('');
    if (isCompact) setExpanded(false);
  };

  // Handle suggestion click based on variant
  const handleSuggestionClick = (car) => {
    setShowSuggestions(false);
    if (isStandalone || isCompact) {
      // Navigate to catalog with car detail
      navigate(`${catalogPath}/${car.id}`);
    } else {
      // Full variant — navigate to car detail
      navigate(`/cars/${car.id}`);
    }
  };

  // Handle "Show all results" click
  const handleShowAll = () => {
    setShowSuggestions(false);
    if (onSubmitQuery) {
      onSubmitQuery(inputValue.trim());
    } else if (isStandalone || isCompact) {
      navigate(
        `${catalogPath}?search=${encodeURIComponent(inputValue.trim())}`
      );
    }
  };

  // Handle Enter key — navigate to catalog with search query
  const handleKeyDown = (e) => {
    // С `onSubmitQuery` Enter никогда не отправляет родительскую форму — и
    // при коротком запросе тоже: иначе один символ уводил форму героя в
    // каталог мимо колбэка.
    if (e.key === 'Enter' && onSubmitQuery) e.preventDefault();
    if (e.key === 'Enter' && inputValue.trim().length >= 2) {
      setShowSuggestions(false);
      if (onSubmitQuery) {
        onSubmitQuery(inputValue.trim());
      } else if (isStandalone || isCompact) {
        navigate(
          `${catalogPath}?search=${encodeURIComponent(inputValue.trim())}`
        );
      }
    }
    if (e.key === 'Escape') {
      setShowSuggestions(false);
      if (isCompact) {
        setExpanded(false);
        setInputValue('');
      }
    }
  };

  // Available cars only in the dropdown (#1221) — same rule as the standalone
  // `registry/Search` embed: sources that opted in show reserved cars on the
  // RESULTS page (badged by CarCard), never in these unbadged rows. Filter before
  // slicing so the 6 rows still fill up when top hits are reserved.
  //
  // Note this component's `onSearchResults` still hands the parent the UNFILTERED
  // result — the catalog needs the full hit set to build its filter/rank map, and
  // that's exactly the surface allowed to show reserved cars.
  const {
    rows: suggestions,
    totalHits,
    showAll,
  } = useMemo(() => deriveSuggestions(results, SUGGESTION_LIMIT), [results]);

  const isActive = inputValue && inputValue.trim().length >= 2;
  const placeholderText =
    placeholder ||
    t('showcase.searchBar.placeholder', 'Search cars, e.g. Mercedes SUV');

  // === COMPACT VARIANT ===
  if (isCompact && !expanded) {
    return (
      <button
        type="button"
        ref={containerRef}
        onClick={() => {
          setExpanded(true);
          setTimeout(() => inputRef.current?.focus(), 100);
        }}
        className="search-bar__toggle"
        title={t('showcase.searchBar.open', 'Search cars')}
        aria-label={t('showcase.searchBar.open', 'Search cars')}
      >
        <Search size={20} />
      </button>
    );
  }

  // === FULL / STANDALONE / EXPANDED COMPACT ===
  return (
    <div
      className={`search-bar${isCompact ? ' search-bar--compact' : ''}`}
      ref={containerRef}
    >
      <div className="search-bar__icon">
        {searching ? (
          <Loader2 className="search-bar__spinner" />
        ) : (
          <Search size={16} />
        )}
      </div>
      <input
        ref={inputRef}
        type="text"
        value={inputValue}
        onChange={(e) => {
          hasUserTypedRef.current = true;
          setInputValue(e.target.value);
          setShowSuggestions(true);
          if (onQueryChange) onQueryChange(e.target.value);
        }}
        onFocus={() => {
          if (results && isActive) setShowSuggestions(true);
        }}
        onKeyDown={handleKeyDown}
        placeholder={placeholderText}
        // Своей подписи у поля нет — без имени читалка объявляла его просто
        // «поле редактирования», а плейсхолдер именем не считается.
        aria-label={placeholderText}
        className="search-bar__input"
        autoFocus={isCompact}
      />
      <div className="search-bar__actions">
        {inputValue && (
          <button
            type="button"
            onClick={handleClear}
            className="search-bar__action"
            title={t('showcase.searchBar.clear', 'Clear search')}
            aria-label={t('showcase.searchBar.clear', 'Clear search')}
          >
            <X size={16} />
          </button>
        )}
        {voiceAssistant && (
          <button
            type="button"
            onClick={() => {
              setShowSuggestions(false);
              // Issue #944 Phase 4: migrated to openChat (unified entry point).
              voiceAssistant.openChat({
                mode: 'chat',
                action: 'search_with_ai',
                metadata: inputValue.trim()
                  ? { searchQuery: inputValue.trim() }
                  : undefined,
              });
            }}
            className="search-bar__action search-bar__action--ai"
            title={t('showcase.search.askAi', 'Ask the assistant')}
            aria-label={t('showcase.search.askAi', 'Ask the assistant')}
          >
            <Sparkles size={16} />
          </button>
        )}
      </div>

      {/* Suggestions dropdown */}
      {showSuggestions && isActive && force && unavailable && !searching && (
        <div className="search-bar__dropdown">
          <div className="search-bar__status">
            {t(
              'showcase.searchBar.unavailable',
              "Search isn't working right now. Try again in a minute."
            )}
          </div>
        </div>
      )}

      {showSuggestions &&
        isActive &&
        (searching || (results && results.hits.length > 0)) && (
          <div className="search-bar__dropdown">
            {searching && (
              <div className="search-bar__status">
                <Loader2 className="search-bar__spinner" />
                {t('showcase.searchBar.searching', 'Searching…')}
              </div>
            )}
            {results && !searching && (
              <div className="search-bar__group-title">
                {/* Без времени поиска в миллисекундах: строка стоит и на
                    главной, у покупателя, а ему это служебная цифра, не
                    ответ. Счёт результатов — то, что ему нужно. */}
                {t(
                  totalHits === 1
                    ? 'showcase.searchBar.resultsOne'
                    : 'showcase.searchBar.resultsMany',
                  totalHits === 1 ? '{count} result' : '{count} results'
                ).replace('{count}', totalHits)}
              </div>
            )}
            {results &&
              suggestions.map((car) => (
                <button
                  type="button"
                  key={car.meiliId || car.id}
                  className="search-bar__item"
                  onClick={() => handleSuggestionClick(car)}
                >
                  <CarSuggestionThumbnail car={car} />
                  <div className="search-bar__item-body">
                    <div className="search-bar__item-title">
                      {car.make} {car.model}
                    </div>
                    <div className="search-bar__item-meta">
                      {formatRegYear(car)} &middot;{' '}
                      {plainNumber(car.mileage)} km &middot;{' '}
                      {translateVehicleValue(car.fuelType, 'fuelType')}
                    </div>
                  </div>
                  <div className="search-bar__item-price">
                    {car.price > 0
                      ? money(car.price)
                      : t(
                          'showcase.searchBar.priceOnRequest',
                          'Price on request'
                        )}
                  </div>
                </button>
              ))}
            {results && showAll && (
              <button
                type="button"
                className="search-bar__more"
                onClick={handleShowAll}
              >
                {t('showcase.searchBar.showAll', 'Show all ({count})').replace(
                  '{count}',
                  totalHits
                )}
              </button>
            )}
          </div>
        )}
    </div>
  );
};

export default SearchBar;
